import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize} from 'rxjs/operators';
import {FolderService} from '../../../services/folder/folder.service';
import {NotificationService} from '../../../services/common/notification.service';
import {FOLDER_PERMISSION_LABELS, FolderPermission, FolderShare, folderUserName} from '../../../dtos/folder/folder';
import {folderErrorMessage} from '../../../shared/folder/folder-utils';
import {ConfirmationDialogComponent} from '../../common/confirmation-dialog/confirmation-dialog.component';

export interface FolderShareDialogData {
  folderId: number;
  folderName: string;
}

/**
 * lets the owner share a Sammelmappe, change the permission of shares and revoke them.
 * Closes with `true` if anything changed.
 */
@Component({
  selector: 'app-folder-share-dialog',
  templateUrl: './folder-share-dialog.component.html',
  styleUrls: ['./folder-share-dialog.component.scss'],
  standalone: false
})
export class FolderShareDialogComponent implements OnInit {

  readonly permissions: FolderPermission[] = ['VIEW', 'EDIT'];
  readonly permissionLabels = FOLDER_PERMISSION_LABELS;
  readonly displayedColumns = ['user', 'permission', 'status', 'revoke'];

  shareForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    permission: new FormControl<FolderPermission>('VIEW', Validators.required)
  });
  shares: FolderShare[] = [];
  loading = true;
  saving = false;
  changed = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: FolderShareDialogData,
              private dialog: MatDialog,
              private folderService: FolderService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.loadShares();
  }

  /**
   * name of the recipient once the share is accepted, before that only the invited address is known
   */
  recipientName(share: FolderShare): string {
    return share.accepted && share.user ? folderUserName(share.user) : share.email;
  }

  loadShares(): void {
    this.loading = true;
    this.folderService.getShares(this.data.folderId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: shares => this.shares = shares,
        error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
      });
  }

  share(): void {
    // pasted addresses often contain surrounding spaces, which the email validator rejects
    const emailCtrl = this.shareForm.controls.email;
    if (emailCtrl.value !== emailCtrl.value?.trim()) {
      emailCtrl.setValue(emailCtrl.value.trim());
    }
    if (this.shareForm.invalid || this.saving) {
      this.shareForm.markAllAsTouched();
      return;
    }
    const email = this.shareForm.value.email.trim();
    const permission = this.shareForm.value.permission;
    this.saving = true;
    this.folderService.share(this.data.folderId, email, permission)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: share => {
          const existing = this.shares.findIndex(s => s.id === share.id);
          if (existing >= 0) {
            this.shares[existing] = share;
            this.shares = [...this.shares];
            this.notificationService.openSnackBar('Berechtigung für ' + share.email + ' geändert.');
          } else {
            this.shares = [...this.shares, share];
            this.notificationService.openSnackBar('Einladung an ' + share.email + ' gespeichert.');
          }
          this.changed = true;
          this.shareForm.reset({email: '', permission});
        },
        error: error => this.notificationService.openSnackBar(this.shareErrorMessage(error))
      });
  }

  private shareErrorMessage(error: HttpErrorResponse): string {
    const details: string = error.error?.errors?.[0] ?? '';
    if (error.status === 400 && details.includes('at most')) {
      return 'Eine Sammelmappe kann mit höchstens 50 Personen geteilt werden.';
    }
    return folderErrorMessage(error, {
      400: 'Bitte geben Sie eine gültige E-Mail-Adresse einer anderen Person an. '
        + 'Mit sich selbst können Sie eine Sammelmappe nicht teilen.',
      429: 'Sie haben in den letzten 24 Stunden sehr viele Einladungen verschickt. '
        + 'Bitte versuchen Sie es später erneut.'
    });
  }

  changePermission(share: FolderShare, permission: FolderPermission): void {
    const previous = share.permission;
    share.permission = permission;
    this.folderService.updateShare(this.data.folderId, share.id, permission).subscribe({
      next: updated => {
        Object.assign(share, updated);
        this.changed = true;
      },
      error: error => {
        share.permission = previous;
        this.notificationService.openSnackBar(folderErrorMessage(error));
      }
    });
  }

  revoke(share: FolderShare): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Freigabe entfernen',
        content: this.recipientName(share) + ' hat danach keinen Zugriff mehr auf die Sammelmappe „'
          + this.data.folderName + '“.',
        action: 'Entfernen'
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.folderService.deleteShare(this.data.folderId, share.id).subscribe({
        next: () => {
          this.shares = this.shares.filter(s => s.id !== share.id);
          this.changed = true;
          this.notificationService.openSnackBar('Freigabe für ' + share.email + ' entfernt.');
        },
        error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
      });
    });
  }
}
